// packages import
import { Menu, MenuItem } from '@mui/material'
import { FC, MouseEvent, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// component import
import { ConfirmationModal } from '../common/ConfirmationModal'

// others
import { BillStatus, useDownloadBillSnapshotLazyQuery } from '../../../generated'
import { BillsMenuProps } from '../../interfaceTypes'
import { AuthContext } from '../../context'
import { copyTextToClipboard, hasAdminAccess } from '../../utils'
import { useBillActions } from '../../hooks/useBillActions'
import { BILL_VOID_BLOCKED_BY_PAYMENTS_MESSAGE, FINAL_BILL_STATUSES, PRE_BILL_STATUSES } from '../../constants'
import { RecordPaymentModal } from '../billing/billDetail/modal/RecordPayment'
import { WriteOffBillsModal } from '../billing/billDetail/modal/WriteOffBillsModal'
import { CarryForwardInterestModal } from '../billing/billDetail/modal/CarryForwardInterestModal'
import { EditBillDueDateModal } from '../billing/billDetail/modal/EditBillDueDateModal'
import {
  canCarryForwardInterest,
  canShowVoidBillAction,
  canViewBillActions,
  canWriteOffBill,
  generateBillPaymentUrl,
  hasAppliedPayments
} from '../../utils/billing'
import { Alert } from '../common/Alert'
import { printBillsV2 } from '../../../services'
import { gql, useMutation } from '@apollo/client'

const MARK_BILL_DELIVERED = gql`
  mutation MarkBillAsDelivered($billId: Int!) {
    markBillAsDelivered(billId: $billId) {
      response {
        status
        message
      }
    }
  }
`

export const BillsMenu: FC<BillsMenuProps> = ({
  bill,
  account,
  caseId,
  clientId,
  clientPayableAmount,
  monthlyFlatFeeAmount,
  billingType,
  billApprover,
  isHourlyCase,
  isContingencyCase,
  onRefetch,
  renderAction,
  hideViewOption,
  markDeliveredOnPrint,
  canEdit
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [showSendToClientConfirmation, setShowSendToClientConfirmation] = useState(false)
  const [showSendReminderConfirmation, setShowSendReminderConfirmation] = useState(false)
  const [showMarkDeliveredConfirmation, setShowMarkDeliveredConfirmation] = useState(false)
  const [showCarryForwardInterest, setShowCarryForwardInterest] = useState(false)
  const [showEditDueDate, setShowEditDueDate] = useState(false)
  const [showVoidBlocked, setShowVoidBlocked] = useState(false)
  const [downloadingOriginal, setDownloadingOriginal] = useState(false)

  const {
    // State
    departmentHead,
    isDepartmentHead,
    showVoidConfirmation,
    setShowVoidConfirmation,
    voidBlockedMessage,
    setVoidBlockedMessage,
    showRecordPaymentModal,
    setShowRecordPaymentModal,
    showWriteOffConfirmation,
    setShowWriteOffConfirmation,
    showAdminApprovalConfirmation,
    setShowAdminApprovalConfirmation,
    sendingForApproval,
    approvingByAttorney,
    approvingByDepartmentHead,
    voiding,
    sendingToClient,
    sendingReminderToClient,

    // Actions
    handleSendForApproval,
    handleApproveByAttorney,
    handleApproveByDepartmentHead,
    handleVoidBill,
    handleSendBillToClient,
    fetchDepartmentHead,
    checkIsUserDepartmentHeadForCase,
    handleSendingReminderToClient
  } = useBillActions({
    onRefetch,
    onClose: () => {
      setAnchorEl(null)
      setShowSendToClientConfirmation(false)
      setShowSendReminderConfirmation(false)
    }
  })
  const { currentUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const [downloadBillSnapshot] = useDownloadBillSnapshotLazyQuery({ fetchPolicy: 'network-only' })

  const [markBillDelivered, { loading: markingDelivered }] = useMutation(MARK_BILL_DELIVERED, {
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      Alert.success(data?.markBillAsDelivered?.response?.message || 'Bill marked as delivered to client')
      setShowMarkDeliveredConfirmation(false)
      setAnchorEl(null)
      onRefetch?.()
    }
  })

  const open = Boolean(anchorEl)

  const amountPaid = Math.max(
    bill?.amountPaid || 0,
    // Bill payments
    bill?.paymentAllocations?.reduce((acc, paymentAllocation) => acc + paymentAllocation.amount, 0) || 0
  )
  const showBillActions =
    bill?.status &&
    currentUser &&
    canViewBillActions(billApprover?.id || null, isDepartmentHead, bill.status, currentUser)
  const canSendForApproval = hasAdminAccess(currentUser) && bill?.status === BillStatus.Draft
  const canApproveByAttorney =
    billApprover?.id === currentUser?.id &&
    (bill?.status === BillStatus.Draft || bill?.status === BillStatus.SentForApproval)
  const canApproveByDepartmentHead = isDepartmentHead && bill?.status === BillStatus.ApprovedByResponsibleAttorney
  const canSendBill = hasAdminAccess(currentUser) && bill?.status === BillStatus.Billed && bill?.sendToClient
  const canRecordPayment =
    hasAdminAccess(currentUser) &&
    (bill?.status === BillStatus.Billed ||
      bill?.status === BillStatus.SentToClient ||
      bill?.status === BillStatus.PartiallyPaid)

  // Void stays visible even when payments are applied — clicking it explains why it's blocked and
  // what to do about it, rather than the action quietly not being there.
  const canVoid = canShowVoidBillAction(currentUser, bill?.status)
  const canAdminApproveBill = bill && hasAdminAccess(currentUser) && PRE_BILL_STATUSES.includes(bill.status)

  const canSendReminderToClient =
    hasAdminAccess(currentUser) &&
    bill?.sendToClient &&
    (bill?.status === BillStatus.PartiallyPaid || bill?.status === BillStatus.SentToClient)

  const canMarkDelivered =
    hasAdminAccess(currentUser) &&
    !!bill?.status &&
    [
      BillStatus.Billed,
      BillStatus.SentToClient,
      BillStatus.PartiallyPaid,
      BillStatus.Paid,
      BillStatus.Forwarded
    ].includes(bill.status)

  // A due date only exists once the bill is finalized — finalizing sets it to a fixed number of days
  // out, which is not always the date agreed with the client, so billing can correct it afterwards.
  // Mirrors the roles allowed by the API's UPDATE_BILL_DUE_DATE permission.
  const canEditDueDate =
    hasAdminAccess(currentUser) &&
    !!bill?.status &&
    [
      BillStatus.Billed,
      BillStatus.SentToClient,
      BillStatus.PartiallyPaid,
      BillStatus.Paid,
      BillStatus.Forwarded
    ].includes(bill.status)

  // "Print Pre-Bill" only makes sense on a legitimately issued (final) bill — on a real pre-bill
  // the normal Print already renders the pre-bill form, and on void/written-off/forwarded bills a
  // pre-bill printout would be misleading.
  const canPrintPreBill = !!bill?.status && FINAL_BILL_STATUSES.includes(bill.status)

  const canDownloadOriginal =
    !!bill?.pdfSnapshotS3Key &&
    [
      BillStatus.Billed,
      BillStatus.SentToClient,
      BillStatus.PartiallyPaid,
      BillStatus.Paid,
      BillStatus.Forwarded
    ].includes(bill?.status)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleVoidClick = () => {
    handleClose()

    // A bill can sit in a paid/partially-paid status with nothing actually applied to it (e.g. its
    // only payment was deleted afterwards). Gate on the money still on the bill, not the status.
    if (hasAppliedPayments(amountPaid)) {
      setShowVoidBlocked(true)
      return
    }

    setShowVoidConfirmation(true)
  }

  const handleCancelVoid = () => {
    setShowVoidConfirmation(false)
  }

  const handleCloseVoidBlocked = () => {
    setShowVoidBlocked(false)
    setVoidBlockedMessage(null)
  }

  const handleRecordPaymentClick = () => {
    navigate(`/accounts/deposit-fund?depositType=client&matterId=${caseId}&clientId=${clientId}`)
    // handleClose()
    // setShowRecordPaymentModal(true)
  }

  const handleCloseRecordPaymentModal = () => {
    setShowRecordPaymentModal(false)
  }

  const handleWriteOffClick = () => {
    handleClose()
    setShowWriteOffConfirmation(true)
  }

  const handleCloseWriteOffConfirmation = () => {
    setShowWriteOffConfirmation(false)
  }

  const printBill = async (options?: { asPreBill?: boolean }) => {
    if (bill?.id == null) return
    const billId = typeof bill.id === 'number' ? bill.id : Number(bill.id)
    if (!Number.isFinite(billId)) return
    try {
      const markAsDelivered = !!markDeliveredOnPrint && !options?.asPreBill
      await printBillsV2([billId], { ...options, markAsDelivered })
      handleClose()
      // Printing marked the bill as delivered; refetch so the page reflects it.
      if (markAsDelivered) onRefetch?.()
    } catch (e) {
      Alert.error(
        e instanceof Error ? e.message : `Failed to generate ${options?.asPreBill ? 'pre-bill ' : ''}PDF`
      )
    }
  }

  const handlePrint = () => printBill()

  const handlePrintPreBill = () => printBill({ asPreBill: true })

  const handleDownloadOriginal = async () => {
    if (bill?.id == null) return
    const billId = typeof bill.id === 'number' ? bill.id : Number(bill.id)
    if (!Number.isFinite(billId)) return

    setDownloadingOriginal(true)
    try {
      const { data } = await downloadBillSnapshot({ variables: { billId } })
      const result = data?.downloadBillSnapshot

      if (result?.preSignedUrl) {
        window.open(result.preSignedUrl, '_blank')
        handleClose()
      } else {
        Alert.info(result?.response?.message || 'PDF snapshot is being generated. Please try again in a few minutes.')
        handleClose()
      }
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to download original bill')
    } finally {
      setDownloadingOriginal(false)
    }
  }

  const handleEdit = () => {
    if (typeof bill?.id === 'number') {
      navigate(`/billing/edit-bill/${bill.id}`)
    }
    handleClose()
  }

  const handleView = () => {
    if (bill?.id) {
      const finalBillStatuses = [...FINAL_BILL_STATUSES, BillStatus.Forwarded, BillStatus.WrittenOff]
      navigate(`/billing/bill-detail/${bill.id}`, {
        state: { isFinalBillView: finalBillStatuses.includes(bill.status) }
      })
    }
    handleClose()
  }

  const handleAdminApproval = () => {
    if (!bill) return

    if ([BillStatus.Draft, BillStatus.SentForApproval].includes(bill.status)) {
      handleApproveByAttorney(bill.id)
    }

    if (bill.status === BillStatus.ApprovedByResponsibleAttorney) {
      handleApproveByDepartmentHead(bill.id)
    }
  }

  const getAdminApprovalConfirmationDialogDescription = () => {
    if ([BillStatus.Draft, BillStatus.SentForApproval].includes(bill.status)) {
      return `Are you sure you want to approve this bill on behalf of Attorney ${billApprover?.firstName || ''} ${
        billApprover?.lastName || ''
      }? This action cannot be undone.`
    }

    return `Are you sure you want to approve this bill on behalf of Department Head ${
      departmentHead?.firstName || ''
    } ${departmentHead?.lastName || ''}? This action cannot be undone.`
  }

  useEffect(() => {
    if (bill?.status === BillStatus.ApprovedByResponsibleAttorney && caseId) {
      fetchDepartmentHead(caseId)
    }
    // eslint-disable-next-line
  }, [bill?.status, caseId])

  useEffect(() => {
    if (bill?.status === BillStatus.ApprovedByResponsibleAttorney && caseId && currentUser) {
      checkIsUserDepartmentHeadForCase(caseId, currentUser.id)
    }
    // eslint-disable-next-line
  }, [bill?.status, caseId])

  /**
   * The access token behind the link this client was emailed.
   *
   * A split bill issues one token per client, so the token is matched on the client currently
   * selected on the bill-detail page — copying any other client's link would hand out a page showing
   * someone else's share and their payment form. The single-token fallback covers the ordinary
   * (non-split) bill, where every recipient shares one link and `clientId` may not line up with the
   * token's client.
   *
   * Undefined on the list views, which do not load `accessTokens`, and on bills that were never
   * finalised — tokens are only minted when the bill is issued. The menu item is hidden in both
   * cases rather than copying a broken link.
   */
  const clientBillToken = useMemo(() => {
    const tokens = bill?.accessTokens?.filter(accessToken => !!accessToken?.token) ?? []
    if (!tokens.length) return undefined

    const ownToken = clientId ? tokens.find(accessToken => accessToken.client?.id === clientId) : undefined
    return ownToken?.token ?? (tokens.length === 1 ? tokens[0].token : undefined)
  }, [bill?.accessTokens, clientId])

  /**
   * Copies the client-facing bill link — the same `/bill/view/:token` URL that goes out by email, so
   * what is pasted to a client is exactly what they would have received.
   *
   * Reports the real outcome: the clipboard write can be refused by the browser (permissions policy,
   * or a non-secure context where the fallback also fails), and silently claiming "Copied" would
   * leave someone pasting whatever was on the clipboard before.
   */
  const handleCopyClientBillUrl = async () => {
    handleClose()
    if (!clientBillToken) return

    const copied = await copyTextToClipboard(generateBillPaymentUrl(clientBillToken))
    if (copied) Alert.success('Client bill URL copied to clipboard')
    else Alert.error('Could not copy the URL — your browser blocked clipboard access')
  }

  return (
    <>
      {renderAction(handleClick)}

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!hideViewOption && <MenuItem onClick={handleView}>View</MenuItem>}
        {canEdit && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
        <MenuItem onClick={handlePrint}>Print</MenuItem>

        {canPrintPreBill && <MenuItem onClick={handlePrintPreBill}>Print Pre-Bill</MenuItem>}

        {canDownloadOriginal && (
          <MenuItem onClick={handleDownloadOriginal} disabled={downloadingOriginal}>
            Download Original
          </MenuItem>
        )}

        {clientBillToken && <MenuItem onClick={handleCopyClientBillUrl}>Copy Client Bill URL</MenuItem>}

        {canMarkDelivered && (
          <MenuItem
            onClick={() => {
              handleClose()
              setShowMarkDeliveredConfirmation(true)
            }}
            disabled={markingDelivered}
          >
            Mark as Delivered
          </MenuItem>
        )}

        {canEditDueDate && (
          <MenuItem
            onClick={() => {
              handleClose()
              setShowEditDueDate(true)
            }}
          >
            Edit Due Date
          </MenuItem>
        )}

        {showBillActions && (
          <>
            {canSendBill && (
              <MenuItem
                onClick={() => {
                  handleClose()
                  setShowSendToClientConfirmation(true)
                }}
                disabled={sendingToClient}
              >
                Send To Client
              </MenuItem>
            )}

            {/* "Apply Client Funds" was removed here: applying a client's trust balance is now a
                separate process on the Deposit Funds screen, and the applyClientFunds mutation it
                called is commented out in the API. See docs/ACCRUAL_REMNANTS_AUDIT.md. */}

            {canRecordPayment && clientId && (
              <MenuItem onClick={handleRecordPaymentClick} disabled={!clientPayableAmount}>
                Record Payment
              </MenuItem>
            )}

            {canSendForApproval && (
              <MenuItem onClick={() => handleSendForApproval(bill?.id)} disabled={sendingForApproval}>
                Send for Attorney Approval
              </MenuItem>
            )}

            {canApproveByAttorney && (
              <MenuItem onClick={() => handleApproveByAttorney(bill?.id)} disabled={approvingByAttorney}>
                Approve
              </MenuItem>
            )}

            {canApproveByDepartmentHead && (
              <MenuItem onClick={() => handleApproveByDepartmentHead(bill?.id)} disabled={approvingByDepartmentHead}>
                Approve
              </MenuItem>
            )}

            {canAdminApproveBill && <MenuItem onClick={() => setShowAdminApprovalConfirmation(true)}>Approve</MenuItem>}

            {canSendReminderToClient && clientId && (
              <MenuItem
                onClick={() => {
                  handleClose()
                  setShowSendReminderConfirmation(true)
                }}
                disabled={!clientPayableAmount || sendingReminderToClient}
              >
                Send Reminder To Client
              </MenuItem>
            )}

            {canVoid && (
              <MenuItem onClick={handleVoidClick} disabled={voiding}>
                Void
              </MenuItem>
            )}

            {canWriteOffBill(bill?.status, currentUser) && clientId && (
              <MenuItem onClick={handleWriteOffClick}>Write Off balance</MenuItem>
            )}

            {canCarryForwardInterest(bill?.status, currentUser) && clientId && (
              <MenuItem
                onClick={() => {
                  handleClose()
                  setShowCarryForwardInterest(true)
                }}
              >
                Carry Forward Interest
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {showSendToClientConfirmation && (
        <ConfirmationModal
          isOpen={showSendToClientConfirmation}
          title="Send Bill to Client"
          message={`Are you sure you want to send bill #${bill?.id} to the client? An email/text will be delivered based on the matter's delivery settings.`}
          confirmText="Send"
          cancelText="Cancel"
          variant="danger"
          isLoading={sendingToClient}
          onConfirm={() => handleSendBillToClient(bill?.id)}
          onCancel={() => setShowSendToClientConfirmation(false)}
        />
      )}

      {showSendReminderConfirmation && (
        <ConfirmationModal
          isOpen={showSendReminderConfirmation}
          title="Send Payment Reminder"
          message={`Are you sure you want to send a payment reminder for bill #${bill?.id} to the client?`}
          confirmText="Send Reminder"
          cancelText="Cancel"
          variant="danger"
          isLoading={sendingReminderToClient}
          onConfirm={() => handleSendingReminderToClient(bill?.id)}
          onCancel={() => setShowSendReminderConfirmation(false)}
        />
      )}

      {showMarkDeliveredConfirmation && (
        <ConfirmationModal
          isOpen={showMarkDeliveredConfirmation}
          title="Mark Bill as Delivered"
          message={
            `Confirm that bill #${bill?.id} has been delivered to the client.\n\n` +
            'This marks the bill as sent to the client and records the sent date. This cannot be undone.'
          }
          confirmText="Mark as Delivered"
          cancelText="Cancel"
          variant="danger"
          isLoading={markingDelivered}
          onConfirm={() => bill?.id != null && markBillDelivered({ variables: { billId: Number(bill.id) } })}
          onCancel={() => setShowMarkDeliveredConfirmation(false)}
        />
      )}

      {showVoidConfirmation && (
        <ConfirmationModal
          isOpen={showVoidConfirmation}
          title="Void Bill"
          message={`Are you sure you want to void bill #${bill?.id}? This action cannot be undone.`}
          confirmText="Void Bill"
          cancelText="Cancel"
          variant="danger"
          isLoading={voiding}
          onConfirm={() => handleVoidBill(bill?.id)}
          onCancel={handleCancelVoid}
        />
      )}

      {(showVoidBlocked || !!voidBlockedMessage) && (
        <ConfirmationModal
          isOpen
          title="Bill Cannot Be Voided"
          message={voidBlockedMessage || BILL_VOID_BLOCKED_BY_PAYMENTS_MESSAGE}
          cancelText="Close"
          variant="warning"
          showConfirmButton={false}
          onConfirm={handleCloseVoidBlocked}
          onCancel={handleCloseVoidBlocked}
        />
      )}

      {showAdminApprovalConfirmation && (
        <ConfirmationModal
          isOpen={showAdminApprovalConfirmation}
          title="Approve Bill"
          message={getAdminApprovalConfirmationDialogDescription()}
          confirmText="Approve"
          cancelText="Cancel"
          variant="danger"
          isLoading={approvingByAttorney || approvingByDepartmentHead}
          onConfirm={() => handleAdminApproval()}
          onCancel={() => setShowAdminApprovalConfirmation(false)}
        />
      )}

      {showWriteOffConfirmation && clientId && caseId && (
        <WriteOffBillsModal
          isOpen={showWriteOffConfirmation}
          billId={bill.id}
          clientId={clientId}
          onClose={handleCloseWriteOffConfirmation}
          onSuccess={onRefetch}
        />
      )}

      {showCarryForwardInterest && clientId && (
        <CarryForwardInterestModal
          isOpen={showCarryForwardInterest}
          billId={bill.id}
          clientId={clientId}
          onClose={() => setShowCarryForwardInterest(false)}
          onSuccess={onRefetch}
        />
      )}

      {showRecordPaymentModal && clientId && (
        <RecordPaymentModal
          isOpen={showRecordPaymentModal}
          billId={bill.id}
          caseId={caseId}
          billingType={billingType}
          account={account}
          clientId={clientId}
          clientPayableAmount={clientPayableAmount}
          monthlyFlatFeeAmount={monthlyFlatFeeAmount}
          onClose={handleCloseRecordPaymentModal}
          onSuccess={onRefetch}
        />
      )}
    </>
  )
}
